import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import AppTextInput from '../AppTextInput';
import type { DeliveryAddress, DeliveryQuoteResult } from '../../types/delivery';
import { formatCentsUsd, formatDeliveryAddressLine } from '../../types/delivery';
import { quoteDeliveryCheckout } from '../../services/deliveryCheckout';
import { DELIVERY_FEES_LABEL } from '../../constants/deliveryConfig';
import {
  isCompleteDeliveryAddress,
} from '../../utils/deliveryAddressFromProfile';
import {
  loadSavedDeliveryAddress,
  saveDeliveryAddressForFuture,
} from '../../services/savedDeliveryAddressService';

interface DeliveryCheckoutPanelProps {
  businessId: string;
  subtotalCents: number;
  customerPhone?: string;
  customerEmail?: string;
  userId?: string;
  userAddress?: unknown;
  onQuote: (quote: DeliveryQuoteResult, address: DeliveryAddress, tipCents: number) => void;
}

const TIP_OPTIONS_CENTS = [0, 200, 300, 500];

function applyAddressToFields(
  addr: DeliveryAddress,
  setters: {
    setStreet: (v: string) => void;
    setCity: (v: string) => void;
    setState: (v: string) => void;
    setZip: (v: string) => void;
    setInstructions: (v: string) => void;
    setPhone: (v: string) => void;
  }
) {
  setters.setStreet(addr.street);
  setters.setCity(addr.city);
  setters.setState(addr.state);
  setters.setZip(addr.zip);
  setters.setInstructions(addr.instructions || '');
  setters.setPhone(addr.phone || '');
}

const DeliveryCheckoutPanel: React.FC<DeliveryCheckoutPanelProps> = ({
  businessId,
  subtotalCents,
  customerPhone,
  customerEmail,
  userId,
  userAddress,
  onQuote,
}) => {
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [instructions, setInstructions] = useState('');
  const [phone, setPhone] = useState(customerPhone || '');
  const [tipCents, setTipCents] = useState(300);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState('');
  const [quote, setQuote] = useState<DeliveryQuoteResult | null>(null);
  const [usingSavedAddress, setUsingSavedAddress] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [saveForFuture, setSaveForFuture] = useState(false);
  const [savedAddressLabel, setSavedAddressLabel] = useState('');
  const autoQuoteDoneRef = useRef(false);
  const canSaveToProfile = !!userId && userId !== 'guest_embed' && userId !== 'guest_app';

  const buildAddress = useCallback((): DeliveryAddress => ({
    street: street.trim(),
    city: city.trim(),
    state: state.trim(),
    zip: zip.trim(),
    country: 'US',
    instructions: instructions.trim(),
    phone: (phone.trim() || customerPhone?.trim() || '').trim() || undefined,
  }), [street, city, state, zip, instructions, phone, customerPhone]);

  const runQuote = useCallback(
    async (address: DeliveryAddress, options?: { saveAfter?: boolean }) => {
      setError('');
      if (!isCompleteDeliveryAddress(address)) {
        setError('Enter your full delivery address.');
        return;
      }
      const dropoffPhone = address.phone || customerPhone?.trim();
      if (!dropoffPhone) {
        setError('Phone number is required for delivery.');
        return;
      }

      setLoading(true);
      const result = await quoteDeliveryCheckout({
        businessId,
        subtotalCents,
        deliveryAddress: address,
        dropoffPhone,
        tipCents,
      });
      setLoading(false);

      if (!result.success) {
        setError(result.error || 'Delivery not available.');
        setQuote(null);
        return;
      }

      const withPhone = { ...address, phone: dropoffPhone };
      setQuote(result);
      onQuote(result, withPhone, tipCents);

      if (options?.saveAfter && saveForFuture) {
        try {
          await saveDeliveryAddressForFuture({
            userId: canSaveToProfile ? userId : undefined,
            guestEmail: customerEmail?.trim() || undefined,
            address: withPhone,
          });
        } catch {
          /* non-blocking */
        }
      }
    },
    [
      businessId,
      subtotalCents,
      customerPhone,
      customerEmail,
      tipCents,
      onQuote,
      saveForFuture,
      canSaveToProfile,
      userId,
    ]
  );

  const handleGetQuote = async () => {
    const address = buildAddress();
    await runQuote(address, { saveAfter: true });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setInitializing(true);
      autoQuoteDoneRef.current = false;
      const saved = await loadSavedDeliveryAddress({
        userId: canSaveToProfile ? userId : undefined,
        userAddress,
        fallbackPhone: customerPhone,
        guestEmail: customerEmail?.trim() || undefined,
      });

      if (cancelled) return;

      if (saved) {
        applyAddressToFields(saved.address, {
          setStreet,
          setCity,
          setState,
          setZip,
          setInstructions,
          setPhone,
        });
        setUsingSavedAddress(true);
        setShowAddressForm(false);
        setSavedAddressLabel(
          saved.source === 'profile' ? 'From your profile' : 'Saved address'
        );
        setSaveForFuture(false);
        setInitializing(false);
        await runQuote(saved.address);
        if (!cancelled) autoQuoteDoneRef.current = true;
        return;
      }

      setUsingSavedAddress(false);
      setShowAddressForm(true);
      setPhone((prev) => prev || customerPhone || '');
      setSaveForFuture(canSaveToProfile || !!customerEmail?.trim());
      setInitializing(false);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when identity / profile changes
  }, [userId, userAddress, customerPhone, customerEmail, canSaveToProfile]);

  // Guest may enter email on the same step — pick up device-saved address when email appears.
  useEffect(() => {
    if (initializing || usingSavedAddress || !customerEmail?.trim()) return;
    let cancelled = false;
    (async () => {
      const saved = await loadSavedDeliveryAddress({
        guestEmail: customerEmail.trim(),
        fallbackPhone: customerPhone,
      });
      if (cancelled || !saved) return;
      applyAddressToFields(saved.address, {
        setStreet,
        setCity,
        setState,
        setZip,
        setInstructions,
        setPhone,
      });
      setUsingSavedAddress(true);
      setShowAddressForm(false);
      setSavedAddressLabel('Saved address');
      setSaveForFuture(false);
      await runQuote(saved.address);
      if (!cancelled) autoQuoteDoneRef.current = true;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerEmail, initializing, usingSavedAddress]);

  useEffect(() => {
    if (!autoQuoteDoneRef.current || !usingSavedAddress || showAddressForm) return;
    const address = buildAddress();
    if (!isCompleteDeliveryAddress(address)) return;
    const timer = setTimeout(() => {
      void runQuote(address);
    }, 300);
    return () => clearTimeout(timer);
  }, [tipCents]);

  if (initializing) {
    return (
      <View style={styles.container}>
        <ActivityIndicator color="#007AFF" />
        <Text style={styles.loadingText}>Loading your delivery details…</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {usingSavedAddress && !showAddressForm ? (
        <View style={styles.savedCard}>
          <Text style={styles.sectionTitle}>Deliver to</Text>
          <Text style={styles.savedLine}>{formatDeliveryAddressLine(buildAddress())}</Text>
          {phone.trim() ? (
            <Text style={styles.savedMeta}>Phone: {phone.trim()}</Text>
          ) : null}
          <Text style={styles.savedBadge}>{savedAddressLabel}</Text>
          <TouchableOpacity
            style={styles.changeLink}
            onPress={() => {
              setShowAddressForm(true);
              setUsingSavedAddress(false);
              setQuote(null);
              setSaveForFuture(canSaveToProfile || !!customerEmail?.trim());
            }}
          >
            <Text style={styles.changeLinkText}>Use a different address</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <>
          <Text style={styles.sectionTitle}>Delivery address</Text>
          <Text style={styles.hint}>
            {canSaveToProfile
              ? 'We could not find a complete address on your profile. Add one below.'
              : 'Enter where we should deliver your order.'}
          </Text>
          <AppTextInput style={styles.input} placeholder="Street" value={street} onChangeText={setStreet} />
          <View style={styles.row}>
            <AppTextInput style={[styles.input, styles.flex]} placeholder="City" value={city} onChangeText={setCity} />
            <AppTextInput style={[styles.input, styles.state]} placeholder="ST" value={state} onChangeText={setState} maxLength={2} />
            <AppTextInput style={[styles.input, styles.zip]} placeholder="ZIP" value={zip} onChangeText={setZip} keyboardType="numeric" />
          </View>
          <AppTextInput style={styles.input} placeholder="Apt / instructions (optional)" value={instructions} onChangeText={setInstructions} />
          <AppTextInput style={styles.input} placeholder="Mobile phone" value={phone} onChangeText={setPhone} keyboardType="phone-pad" />

          <TouchableOpacity
            style={styles.saveRow}
            onPress={() => setSaveForFuture((v) => !v)}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: saveForFuture }}
          >
            <View style={[styles.checkbox, saveForFuture && styles.checkboxChecked]}>
              {saveForFuture ? <Text style={styles.checkmark}>✓</Text> : null}
            </View>
            <Text style={styles.saveLabel}>
              {canSaveToProfile
                ? 'Save to my profile for future deliveries'
                : 'Remember this address on this device'}
            </Text>
          </TouchableOpacity>
        </>
      )}

      <Text style={styles.sectionTitle}>Tip your driver</Text>
      <View style={styles.tipRow}>
        {TIP_OPTIONS_CENTS.map((c) => (
          <TouchableOpacity
            key={c}
            style={[styles.tipChip, tipCents === c && styles.tipChipSelected]}
            onPress={() => setTipCents(c)}
          >
            <Text style={[styles.tipText, tipCents === c && styles.tipTextSelected]}>
              {c === 0 ? 'None' : formatCentsUsd(c)}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {(showAddressForm || !usingSavedAddress) && (
        <TouchableOpacity style={styles.quoteButton} onPress={handleGetQuote} disabled={loading}>
          {loading ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.quoteButtonText}>Get delivery estimate</Text>
          )}
        </TouchableOpacity>
      )}

      {usingSavedAddress && !showAddressForm && loading ? (
        <View style={styles.quoteLoading}>
          <ActivityIndicator color="#007AFF" />
          <Text style={styles.loadingText}>Updating delivery estimate…</Text>
        </View>
      ) : null}

      {error ? <Text style={styles.error}>{error}</Text> : null}

      {quote?.success ? (
        <View style={styles.quoteBox}>
          <View style={styles.quoteRow}>
            <Text style={styles.quoteLabel}>{DELIVERY_FEES_LABEL}</Text>
            <Text style={styles.quoteValue}>{formatCentsUsd(quote.deliveryAndFeesCents)}</Text>
          </View>
          {quote.tipCents > 0 ? (
            <View style={styles.quoteRow}>
              <Text style={styles.quoteLabel}>Tip</Text>
              <Text style={styles.quoteValue}>{formatCentsUsd(quote.tipCents)}</Text>
            </View>
          ) : null}
          <View style={[styles.quoteRow, styles.quoteTotalRow]}>
            <Text style={styles.quoteTotalLabel}>Order total</Text>
            <Text style={styles.quoteTotalValue}>{formatCentsUsd(quote.totalCents)}</Text>
          </View>
          <Text style={styles.eta}>Estimated delivery: ~{quote.etaMinutes} min</Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { marginBottom: 16 },
  sectionTitle: { fontSize: 15, fontWeight: '600', marginBottom: 8, marginTop: 8 },
  hint: { fontSize: 13, color: '#666', marginBottom: 8, lineHeight: 18 },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    fontSize: 15,
  },
  row: { flexDirection: 'row', gap: 8 },
  flex: { flex: 1 },
  state: { width: 56 },
  zip: { width: 88 },
  savedCard: {
    borderWidth: 1,
    borderColor: '#c7e2ff',
    backgroundColor: '#f0f7ff',
    borderRadius: 10,
    padding: 14,
    marginBottom: 4,
  },
  savedLine: { fontSize: 15, color: '#1a1a1a', fontWeight: '600', lineHeight: 22 },
  savedMeta: { fontSize: 13, color: '#555', marginTop: 4 },
  savedBadge: { fontSize: 12, color: '#007AFF', marginTop: 6, fontWeight: '600' },
  changeLink: { marginTop: 10, alignSelf: 'flex-start' },
  changeLinkText: { color: '#007AFF', fontSize: 14, fontWeight: '600' },
  saveRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12, gap: 10 },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#007AFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: { backgroundColor: '#007AFF' },
  checkmark: { color: '#fff', fontSize: 14, fontWeight: '700' },
  saveLabel: { flex: 1, fontSize: 14, color: '#333' },
  tipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  tipChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  tipChipSelected: { borderColor: '#007AFF', backgroundColor: '#f0f7ff' },
  tipText: { fontSize: 14, color: '#333' },
  tipTextSelected: { color: '#007AFF', fontWeight: '600' },
  quoteButton: {
    backgroundColor: '#007AFF',
    padding: 14,
    borderRadius: 10,
    alignItems: 'center',
    marginTop: 4,
  },
  quoteButtonText: { color: '#fff', fontWeight: '700' },
  quoteLoading: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 },
  loadingText: { color: '#666', fontSize: 13 },
  error: { color: '#c00', marginTop: 8 },
  quoteBox: {
    marginTop: 12,
    padding: 12,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
  },
  quoteRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  quoteLabel: { color: '#555' },
  quoteValue: { fontWeight: '600' },
  quoteTotalRow: { marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: '#e1e5e9' },
  quoteTotalLabel: { fontWeight: '700', color: '#1a1a1a' },
  quoteTotalValue: { fontWeight: '700', color: '#1a1a1a' },
  eta: { marginTop: 8, color: '#666', fontSize: 13 },
});

export default DeliveryCheckoutPanel;
